/**
 * React Query hooks for the agentic-analytics experiment dashboards API.
 * Stable query keys live under `queryKeys.analytics.experiments.*` so the
 * analytics caches stay isolated from the workflow-service CRUD caches.
 *
 * `staleTime` tracks the server-side cache TTLs: 5 min for read paths and
 * 15 min for the heavier `agent-summary` compose. Detail reads share
 * `EXPERIMENT_ANALYTICS_READ_OPTIONS` (retry/backoff policy, see helper).
 */

import {
  keepPreviousData,
  type QueryClient,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { queryKeys } from '~/api/queryKeys';

import {
  fetchAnalyticsAgentSummary,
  fetchAnalyticsExperimentHealth,
  fetchAnalyticsExperimentList,
  fetchAnalyticsExperimentSummary,
  fetchAnalyticsPerformanceTrend,
} from '../services/analytics.service';
import type {
  AnalyticsExperimentDetailParams,
  AnalyticsExperimentListParams,
  AnalyticsExperimentListResponse,
  AnalyticsPortfolioItem,
} from '../types/analytics.types';
import type { PerformanceMetric } from '../types/experiment.types';
import { EXPERIMENT_ANALYTICS_READ_OPTIONS } from '../utils/snapshot-read-retry';

const FIVE_MINUTES = 5 * 60 * 1000;
const FIFTEEN_MINUTES = 15 * 60 * 1000;

/**
 * Portfolio list augmented with lift_pct + needs_attention.
 * Consumers typically render this alongside the workflow-service list and
 * merge by experiment_id so the table also shows in-flight drafts that
 * haven't been ingested by analytics yet.
 */
export function useAnalyticsExperimentList(params?: AnalyticsExperimentListParams) {
  return useQuery({
    queryKey: queryKeys.analytics.experiments.list(params as unknown as Record<string, unknown>),
    queryFn: ({ signal }) => fetchAnalyticsExperimentList(params, signal),
    staleTime: FIVE_MINUTES,
    // Portfolio panel is a heavy ClickHouse read; don't re-run it on every tab refocus.
    refetchOnWindowFocus: false,
  });
}

/** Page size used when draining the full analytics portfolio for row enrichment. */
const PORTFOLIO_PAGE_SIZE = 100;

/**
 * Drains the full analytics portfolio across all pages and concatenates the
 * items. Used by the workflow-service-backed `/experiments` table when it
 * needs every analytics row (e.g. to attach a `NEEDS_ATTENTION` badge to
 * any row regardless of which workflow-service page it sits on). The
 * fixed-page-size single-call alternative silently dropped rows past the
 * cap once tenants exceeded it — this hook auto-fetches subsequent pages
 * until `(page * page_size) >= total` and returns a flat `items` array.
 */
export function useAnalyticsExperimentListAll(
  params?: Omit<AnalyticsExperimentListParams, 'page' | 'page_size'>,
) {
  const stableParams = useMemo(() => params ?? {}, [params]);
  const query = useInfiniteQuery<
    AnalyticsExperimentListResponse,
    Error,
    AnalyticsExperimentListResponse[],
    ReturnType<typeof queryKeys.analytics.experiments.list>,
    number
  >({
    queryKey: queryKeys.analytics.experiments.list({
      ...(stableParams as Record<string, unknown>),
      __all: true,
    }),
    queryFn: ({ pageParam, signal }) =>
      fetchAnalyticsExperimentList(
        { ...stableParams, page: pageParam, page_size: PORTFOLIO_PAGE_SIZE },
        signal,
      ),
    initialPageParam: 1,
    // Refocus would re-drain every portfolio page against ClickHouse.
    refetchOnWindowFocus: false,
    getNextPageParam: (last) => {
      const fetched = last.page * last.page_size;
      return fetched < last.total ? last.page + 1 : undefined;
    },
    staleTime: FIVE_MINUTES,
    // The infinite query keeps each page's selector return cached so memoise
    // the concatenation here rather than per-render in every consumer.
    select: (data) => data.pages,
  });

  const items = useMemo<AnalyticsPortfolioItem[]>(() => {
    if (!query.data) return [];
    return query.data.flatMap((page) => page.items);
  }, [query.data]);
  const total = query.data?.[0]?.total ?? 0;

  // Auto-advance through remaining pages. The effect re-runs once each
  // fetched page resolves; the loop ends when `hasNextPage` flips false
  // (i.e. `(page * page_size) >= total`).
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  return {
    items,
    total,
    isLoading: query.isLoading,
    isFetching: query.isFetching || query.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
  };
}

/** Single-experiment KPI summary for the Performance tab header. `segment` is the encoded `?segment=` product filter. */
export function useAnalyticsExperimentSummary(
  experimentId: string,
  segment?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.analytics.experiments.summary(experimentId, segment),
    queryFn: ({ signal }) => fetchAnalyticsExperimentSummary(experimentId, segment, signal),
    enabled: enabled && Boolean(experimentId),
    staleTime: FIVE_MINUTES,
    // Snapshot-backed analytics read: shared retry/backoff policy. See helper.
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}

/**
 * Per-arm cumulative lift trend for the Performance tab chart, computed
 * server-side for the given metric. `keepPreviousData` keeps the prior
 * metric's chart on screen (as `isFetching` background-refresh state) while
 * the new metric loads, instead of the chart flashing blank on every dropdown
 * change.
 */
export function useAnalyticsPerformanceTrend(
  experimentId: string,
  metric: PerformanceMetric,
  segment?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.analytics.experiments.performanceTrend(experimentId, metric, segment),
    queryFn: ({ signal }) => fetchAnalyticsPerformanceTrend(experimentId, metric, segment, signal),
    placeholderData: keepPreviousData,
    enabled: enabled && Boolean(experimentId),
    staleTime: FIVE_MINUTES,
    // Snapshot-backed analytics read: shared retry/backoff policy. See helper.
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}

/**
 * Warms the cumulative-trend query for each supplied metric into the shared
 * cache, mirroring `useAnalyticsPerformanceTrend`'s key/staleTime/retry so a
 * metric the user picks next renders from cache instead of flashing the
 * loading overlay. Already-fresh entries (e.g. the current metric) no-op, so
 * it's safe to call on every dropdown open. Wired to the metric `<Select>`'s
 * open event in `ExperimentPerformancePanel`. See docs/experiment-details-prefetch.md.
 */
export function prefetchAnalyticsPerformanceTrends(
  queryClient: QueryClient,
  experimentId: string,
  metrics: readonly PerformanceMetric[],
  segment?: string,
): void {
  if (!experimentId) return;
  for (const metric of metrics) {
    void queryClient.prefetchQuery({
      queryKey: queryKeys.analytics.experiments.performanceTrend(experimentId, metric, segment),
      queryFn: ({ signal }) =>
        fetchAnalyticsPerformanceTrend(experimentId, metric, segment, signal),
      staleTime: FIVE_MINUTES,
      ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
    });
  }
}

/** Outlier-rate / OOS / drift flags + per-reason breakdown for the Health tab. */
export function useAnalyticsExperimentHealth(
  experimentId: string,
  params?: AnalyticsExperimentDetailParams,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.analytics.experiments.health(
      experimentId,
      params as unknown as Record<string, unknown>,
    ),
    queryFn: ({ signal }) => fetchAnalyticsExperimentHealth(experimentId, params, signal),
    enabled: enabled && Boolean(experimentId),
    staleTime: FIVE_MINUTES,
    // Snapshot-backed analytics read: shared retry/backoff policy. See helper.
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}

/** Pre-digested chat-panel headline + notes (server-composed, 15-min TTL). */
export function useAnalyticsAgentSummary(
  experimentId: string,
  params?: AnalyticsExperimentDetailParams,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.analytics.experiments.agentSummary(
      experimentId,
      params as unknown as Record<string, unknown>,
    ),
    queryFn: ({ signal }) => fetchAnalyticsAgentSummary(experimentId, params, signal),
    enabled: enabled && Boolean(experimentId),
    staleTime: FIFTEEN_MINUTES,
    // Snapshot-backed analytics read: shared retry/backoff policy. See helper.
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}
